import { ConversationalAIPortal } from 'components/BaseLayout/components/ConversationalAI/ConversationalAIPortal';
import { appSettings } from 'config/app.settings';
import { useFeature } from 'context/feature/feature.context';
import { FEATURES } from 'models/features.model';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const BASE_URL = appSettings.ENV_CONVERSATIONAL_AI_URL || '';
const IFRAME_ID = 'conversational-ai-iframe';
const STORAGE_KEY_OPENED = 'conversational-ai-opened';
const STORAGE_KEY_CONVERSATION_ID = 'conversational-ai-conversation-id';

interface ConversationalAIState {
  activated: boolean;
  opened: boolean;
  src: string;
  conversationId: string | null;
}

interface ConversationalAIContextType {
  state: ConversationalAIState;
  activate: (initialMessage?: string) => void;
  open: () => void;
  close: () => void;
  minimize: () => void;
  iframeId: string;
}

const ConversationalAIContext = createContext<ConversationalAIContextType | undefined>(undefined);

export const useConversationalAI = () => {
  const context = useContext(ConversationalAIContext);
  if (!context) {
    throw new Error('useConversationalAI must be used within ConversationalAIProvider');
  }
  return context;
};

const getInitialState = (): ConversationalAIState => {
  const conversationId = sessionStorage.getItem(STORAGE_KEY_CONVERSATION_ID);
  if (conversationId) {
    const opened = sessionStorage.getItem(STORAGE_KEY_OPENED) === 'true';
    return {
      activated: true,
      opened,
      src: BASE_URL,
      conversationId,
    };
  }
  return { activated: false, opened: false, src: BASE_URL, conversationId: null };
};

interface ConversationalAIProviderProps {
  children: React.ReactNode;
}

export const ConversationalAIProvider = ({ children }: ConversationalAIProviderProps) => {
  const [state, setState] = useState<ConversationalAIState>(getInitialState);
  const isFeatureEnabled = useFeature(FEATURES.CONVERSATIONAL_AI);

  useEffect(() => {
    if (state.conversationId) {
      sessionStorage.setItem(STORAGE_KEY_CONVERSATION_ID, state.conversationId);
      sessionStorage.setItem(STORAGE_KEY_OPENED, String(state.opened));
    } else {
      sessionStorage.removeItem(STORAGE_KEY_CONVERSATION_ID);
      sessionStorage.removeItem(STORAGE_KEY_OPENED);
    }
  }, [state.opened, state.conversationId]);

  const activate = useCallback((initialMessage?: string) => {
    if (!BASE_URL) {
      console.warn('ConversationalAI: ENV_CONVERSATIONAL_AI_URL is not configured');
      return;
    }

    let url = BASE_URL;
    if (initialMessage?.trim()) {
      const urlObj = new URL(BASE_URL);
      urlObj.searchParams.set('initMsg', initialMessage.trim());
      url = urlObj.toString();
    }
    setState({ activated: true, opened: true, src: url, conversationId: null });
  }, []);

  const open = useCallback(() => {
    setState((prev) => ({ ...prev, opened: true }));
  }, []);

  const minimize = useCallback(() => {
    setState((prev) => ({ ...prev, opened: false }));
  }, []);

  const setConversationId = useCallback((id: string) => {
    setState((prev) => ({ ...prev, conversationId: id }));
  }, []);

  const close = useCallback(() => {
    setState({ activated: false, opened: false, src: BASE_URL, conversationId: null });
  }, []);

  return (
    <ConversationalAIContext.Provider value={{ state, activate, open, close, minimize, iframeId: IFRAME_ID }}>
      {children}
      {isFeatureEnabled && state.activated && (
        <ConversationalAIPortal
          opened={state.opened}
          src={state.src}
          iframeId={IFRAME_ID}
          close={close}
          minimize={minimize}
          open={open}
          onNewConversationId={setConversationId}
        />
      )}
    </ConversationalAIContext.Provider>
  );
};

export { ConversationalAIContext };
